// Route: /characters — вход в федерацию с CharacterHub (IEC Hero).
//
// Экран решает ОДНУ задачу: довести человека от «с кем поговорить» до рабочей
// сессии. Порядок с 2026-08 обратный прежнему и это главное решение экрана:
//
//   было:  гейт (возраст → согласие → «связать») ЗАКРЫВАЛ всё, включая то,
//          ради чего соглашаться. Человек подтверждал вслепую или уходил.
//   стало: витрина открыта сразу; возраст и согласие спрашиваются на запуске,
//          когда уже понятно, что именно запускается.
//
// Серверные проверки при этом не ослаблены: `linkAccount` по-прежнему требует
// `assertPartnerAgeGate` + `assertPartnerConsent`, а витрина `official` —
// публичные карточки партнёра (наружу уходит только локаль интерфейса).
// Вся механика запуска — в `useCharacterLaunch`, общем с воркспейсом курса.
//
// ┌─ Композиция ──────────────────────────────────────────────────────────────┐
// │ шапка → вкладки «Каталог» / «Мои разговоры» → «Продолжить» (до 3 живых     │
// │ разговоров) → витрина (сценарии, чипы, тулбар, лента).                     │
// │                                                                            │
// │ «Продолжить» стоит первым потому, что вернуться в разговор — самое частое  │
// │ действие, а раньше оно было за прокруткой всей витрины. У новичка блока    │
// │ нет: пустой список «нет сессий» встречал бы его раньше, чем он поймёт,     │
// │ зачем раздел.                                                             │
// └────────────────────────────────────────────────────────────────────────────┘
//
// Список сессий читается из PowerSync, а не из tRPC: он обязан открываться
// офлайн (CLAUDE.md §14а). Каталог и чат сети требуют и честно это говорят.
//
// URL — состояние экрана: `?tab`, `?q`, `?tags`, `?sort`, `?view`, `?character`.
// Одна Zod-схема на всё, запись — функциональным updater'ом с `replace: true`,
// дефолты в URL не пишутся (ссылка на раздел должна выглядеть `/characters`).

import type { CharacterSession } from "@studyhub/shared/contracts/characters";
import { characterCatalogSortSchema } from "@studyhub/shared/contracts/characters";
import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import {
	Alert,
	AlertDescription,
	AlertTitle,
	Avatar,
	AvatarFallback,
	AvatarImage,
	Button,
} from "@studyhub/ui";
import { SegmentedControl } from "@studyhub/ui/components/segmented-control";
import { SectionHeader } from "@studyhub/ui/components/studyhub/section-header";
import { toast } from "@studyhub/ui/toast";
import {
	IconDotsVertical,
	IconExternalLink,
	IconLink,
	IconMasksTheater,
	IconUnlink,
} from "@tabler/icons-react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CharacterSessionList } from "@/components/characters/session-list";
import {
	type CharacterCatalogFiltersValue,
	MAX_SELECTED_TAGS,
} from "@/components/characters/showcase/catalog-filters";
import {
	CHARACTER_CATALOG_VIEW_KEY,
	CharacterShowcase,
} from "@/components/characters/showcase/character-showcase";
import { useCharacterSessions, useCharactersStatus } from "@/components/characters/use-characters";
import { SECTION_ICONS, SECTION_TINT } from "@/components/navigation/section-icons";
import { ActionMenu } from "@/components/shared/action-menu";
import { toastError } from "@/lib/user-facing-error";

/** Эмблема раздела — из общей карты знаков, а не свой импорт иконки. */
const CharactersIcon = SECTION_ICONS.characters;

import { LIST_PAGE_SHELL } from "@/components/shared/entity-list";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useConfirm } from "@/hooks/use-confirm";
import { useViewMode } from "@/hooks/use-view-mode";
import { api } from "@/lib/trpc-client";

const TABS = ["catalog", "sessions"] as const;
type CharactersTab = (typeof TABS)[number];

/**
 * Сколько живых разговоров показываем в блоке «Продолжить».
 *
 * Восемь, а не три: с переходом на ряд лиц (см. `ContinueBlock`) запись стоит
 * не строку списка, а аватар — они помещаются в одну ленту, и обрезать её на
 * трёх значило бы прятать разговоры без выигрыша по месту.
 */
const CONTINUE_LIMIT = 8;

/**
 * URL-состояние раздела.
 *
 * `topicId` приходит из плитки шага ③ воркспейса. Материалы темы подставляются
 * сервером по `subsectionId` — клиент их не собирает и не отправляет. Основной
 * путь из курса теперь другой (витрина открывается шторкой прямо в воркспейсе,
 * `CharacterLauncherDialog`), но параметр остаётся рабочим: по нему приходят
 * старые ссылки и deep-links.
 *
 * `tags` — строка через запятую, а не повторяющийся параметр: канона массивов в
 * URL в проекте нет, а одна форма записи важнее теоретической чистоты.
 *
 * `character` — открытое превью персонажа (`externalId` у партнёра). Параметр,
 * а не отдельная страница: раздел приватный, SEO-ценности у страницы нет, а по
 * потоку она добавляет лишний хоп «карточка → страница → диалог → чат».
 * Пишется `replace: true`, как и остальные: лишняя запись в истории означала
 * бы, что «назад» после закрытия шторки открывает её снова. Аппаратную «Назад»
 * в Capacitor обрабатывает `useOverlayBackGuard` — он шлёт шторке Escape и
 * историю не трогает, и закрытие само снимает параметр.
 */
const searchSchema = z.object({
	topicId: z.string().uuid().optional(),
	tab: z.enum(TABS).optional(),
	q: z.string().optional(),
	tags: z.string().optional(),
	sort: characterCatalogSortSchema.optional(),
	view: z.enum(["grid", "list"]).optional(),
	character: z.string().optional(),
});

export const Route = createFileRoute("/characters")({
	validateSearch: tolerantSearch(searchSchema),
	component: CharactersPage,
	pendingComponent: PagePendingFallback,
});

function CharactersPage(): React.ReactElement {
	const { t } = useTranslation("characters");
	const intro = useSectionIntro("characters");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const utils = api.useUtils();
	const confirm = useConfirm();

	const status = useCharactersStatus();
	const sessions = useCharacterSessions();
	const linked = status.data?.linked ?? false;
	const tab: CharactersTab = search.tab ?? "catalog";

	// Вид запоминается в localStorage тем же ключом, что и внутри витрины, —
	// URL лишь переопределяет его для конкретной ссылки.
	const [storedView, setStoredView] = useViewMode(CHARACTER_CATALOG_VIEW_KEY);
	const view = search.view ?? storedView;

	const filters: CharacterCatalogFiltersValue = useMemo(
		() => ({
			q: search.q ?? "",
			tags: search.tags ? search.tags.split(",").filter(Boolean).slice(0, MAX_SELECTED_TAGS) : [],
			sort: search.sort ?? "popular",
		}),
		[search.q, search.tags, search.sort],
	);

	// Живые разговоры — те, где человек ХОТЬ ЧТО-ТО сказал.
	//
	// Порог именно `>= 2`, а не `> 0`: первое сообщение сессии — приветствие
	// персонажа, оно появляется само, без участия человека. При `> 0` в
	// «Продолжить» попадал каждый «зашёл посмотреть и закрыл» — у владельца
	// блок состоял из Шрека с одним сообщением и двух одинаковых Йод
	// (2026-08-18). Ход пользователя записывается парой (вопрос + ответ),
	// поэтому любой реальный разговор даёт минимум три.
	//
	// Дальше — ОДНА запись на персонажа, самая свежая. Блок отвечает на вопрос
	// «к кому вернуться», а не «какой из разговоров продолжить»: три Мидори
	// подряд неразличимы на вид (одно лицо, одна подпись) и вытесняют из ленты
	// остальных собеседников. Разговоры никуда не деваются — они на вкладке
	// «Мои разговоры», куда ведёт ссылка справа.
	const recent = useMemo(() => {
		const latestByCharacter = new Map<string, CharacterSession>();
		for (const session of sessions.data) {
			if (session.messageCount < 2) continue;
			const known = latestByCharacter.get(session.characterExternalId);
			if (!known || sessionRecency(session) > sessionRecency(known)) {
				latestByCharacter.set(session.characterExternalId, session);
			}
		}
		return [...latestByCharacter.values()]
			.sort((a, b) => sessionRecency(b) - sessionRecency(a))
			.slice(0, CONTINUE_LIMIT);
	}, [sessions.data]);

	const unlink = api.characters.unlinkAccount.useMutation({
		onSuccess: () => void utils.characters.status.invalidate(),
		onError: (err) => toastError(err, t("errors.generic", { ns: "common" }), t),
	});
	const relink = api.characters.linkAccount.useMutation({
		onSuccess: () => {
			void utils.characters.status.invalidate();
			toast.success(t("unlinked.relinked"));
		},
		onError: (err) => toastError(err, t("errors.generic", { ns: "common" }), t),
	});

	const deepLink = api.characters.deepLink.useMutation({
		onSuccess: (result) => {
			window.open(result.url, "_blank", "noopener,noreferrer");
		},
		onError: (err) => toastError(err, t("errors.generic", { ns: "common" }), t),
	});

	function patchSearch(patch: Record<string, string | undefined>): void {
		void navigate({
			search: (prev) => ({ ...prev, ...patch }) as never,
			replace: true,
		});
	}

	function patchFilters(patch: Partial<CharacterCatalogFiltersValue>): void {
		const next: Record<string, string | undefined> = {};
		// Пустое значение и дефолтная сортировка в URL не пишутся.
		if ("q" in patch) next.q = patch.q?.trim() ? patch.q : undefined;
		if ("tags" in patch)
			next.tags = patch.tags && patch.tags.length > 0 ? patch.tags.join(",") : undefined;
		if ("sort" in patch) next.sort = patch.sort === "popular" ? undefined : patch.sort;
		patchSearch(next);
	}

	function deleteSession(id: string): void {
		// Реальная мутация, не оптимистичное удаление из локального стейта:
		// строку из SQLite уберёт синк после серверного DELETE.
		void utils.client.characters.deleteSession
			.mutate({ id })
			.then(() => toast.success(t("sessions.deleted")))
			.catch((err: Error) => toastError(err, t("errors.generic", { ns: "common" }), t));
	}

	if (status.isLoading || !status.data) return <PagePendingFallback />;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<CharactersIcon size={18} />}
				tint={SECTION_TINT.characters}
				title={t("title")}
				description={t("description")}
				stackActions
				actions={
					<>
						<Button
							variant="outline"
							size="sm"
							onClick={() => deepLink.mutate({ target: "home" })}
							disabled={deepLink.isPending}
							className="text-xs max-sm:flex-1"
						>
							<IconExternalLink size={14} stroke={1.75} className="mr-1.5" />
							{t("preview.openHero")}
						</Button>
						<SectionIntroReopen intro={intro} />
						{/* Отвязка — редкое и необратимое без повторной связи действие:
						    прячем в ⋮ и спрашиваем подтверждение, чтобы не сорвать сессии
						    случайным тапом по «Отвязать» в шапке. */}
						{linked ? (
							<ActionMenu
								title={t("actions.menu")}
								items={[
									{
										key: "unlink",
										label: t("actions.unlink"),
										icon: <IconUnlink size={16} />,
										tone: "destructive",
										disabled: unlink.isPending,
										onSelect: () => {
											void (async () => {
												const ok = await confirm({
													title: t("actions.unlinkConfirmTitle"),
													description: t("actions.unlinkConfirmDescription"),
													confirmLabel: t("actions.unlinkConfirm"),
													tone: "destructive",
												});
												if (ok) unlink.mutate();
											})();
										},
									},
								]}
								trigger={
									<Button
										size="icon"
										variant="ghost"
										aria-label={t("actions.menu")}
										disabled={unlink.isPending}
									>
										<IconDotsVertical size={16} />
									</Button>
								}
							/>
						) : null}
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{/* Связь была и порвана: сессии остаются видимыми — метаданные принадлежат
			    пользователю, прекратилась только передача данных наружу. */}
			{!linked && status.data.everLinked ? (
				<Alert variant="default">
					<IconUnlink size={16} />
					<AlertTitle>{t("unlinked.title")}</AlertTitle>
					<AlertDescription className="space-y-3">
						<p>{t("unlinked.description")}</p>
						<Button
							size="sm"
							variant="secondary"
							disabled={relink.isPending}
							onClick={() => relink.mutate()}
						>
							<IconLink size={16} />
							{t("unlinked.relink")}
						</Button>
					</AlertDescription>
				</Alert>
			) : null}

			<SegmentedControl<CharactersTab>
				aria-label={t("tabs.label")}
				value={tab}
				onValueChange={(next) => patchSearch({ tab: next === "catalog" ? undefined : next })}
				options={[
					{ value: "catalog", label: t("tabs.catalog") },
					{
						value: "sessions",
						label: t("tabs.sessions"),
						// Ноль не рисуем: «Мои разговоры 0» — счётчик пустоты, а не
						// информация. `count` показывается даже при 0, если поле задано,
						// поэтому его именно не задаём.
						...(sessions.data.length > 0 ? { count: sessions.data.length } : {}),
					},
				]}
			/>

			{tab === "sessions" ? (
				<CharacterSessionList
					sessions={sessions.data}
					loading={sessions.isLoading}
					onDelete={deleteSession}
				/>
			) : (
				<>
					{recent.length > 0 ? (
						<ContinueBlock sessions={recent} onSeeAll={() => patchSearch({ tab: "sessions" })} />
					) : null}

					<CharacterShowcase
						topicId={search.topicId ?? null}
						filters={filters}
						onFiltersChange={patchFilters}
						view={view}
						onViewChange={(next) => {
							setStoredView(next);
							patchSearch({ view: next });
						}}
						character={search.character ?? null}
						onCharacterChange={(externalId) => patchSearch({ character: externalId ?? undefined })}
					/>
				</>
			)}
		</div>
	);
}

/**
 * Насколько свеж разговор.
 *
 * `lastMessageAt` пуст у сессии, в которой ещё не было ни одного хода, — тогда
 * считаем по дате создания, иначе такая запись всегда проигрывала бы сортировку
 * и «последний разговор с персонажем» показывал бы позапрошлый.
 */
function sessionRecency(session: CharacterSession): number {
	return new Date(session.lastMessageAt ?? session.createdAt).getTime();
}

/**
 * «Продолжить» — лица, а не строки.
 *
 * ── Почему ряд аватаров, а не список ────────────────────────────────────
 * Разговор опознают по лицу собеседника, а не по счётчику сообщений. Список
 * строк с одинаковым мета-текстом («3 сообщения») читался как таблица логов и
 * занимал экран целиком, оттесняя каталог — ровно та витрина, на которую
 * жаловался владелец (2026-08-18). В Hero это решено рядом аватаров
 * «Быстрый доступ», и раз оба продукта — одна экосистема, механика узнавания
 * должна быть та же.
 *
 * Полный список никуда не делся: он на вкладке «Мои разговоры», куда ведёт
 * ссылка справа — это её единственная работа.
 */
function ContinueBlock({
	sessions,
	onSeeAll,
}: {
	sessions: readonly CharacterSession[];
	onSeeAll: () => void;
}): React.ReactElement {
	const { t } = useTranslation("characters");

	return (
		<section className="space-y-3">
			<div className="flex items-center justify-between gap-2">
				<h2 className="flex min-w-0 items-center gap-2 font-medium text-foreground text-sm">
					<IconMasksTheater size={16} />
					{t("continue.title")}
				</h2>
				<Button variant="ghost" size="sm" className="shrink-0 cursor-pointer" onClick={onSeeAll}>
					{t("continue.seeAll")}
				</Button>
			</div>
			{/* Горизонтальная лента: на узком экране лица уезжают за край и
			    скроллятся, а не переносятся в сетку и не распирают вёрстку. */}
			<div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
				{sessions.map((session) => (
					<Link
						key={session.id}
						to="/characters/sessions/$id"
						params={{ id: session.id }}
						preload="intent"
						className="group flex w-[4.5rem] shrink-0 cursor-pointer flex-col items-center gap-1.5 rounded-[var(--radius-md)] p-1 text-center outline-none transition-colors duration-fast hover:bg-well focus-visible:ring-2 focus-visible:ring-ring"
					>
						<Avatar className="size-14 rounded-[var(--radius-lg)] ring-1 ring-border transition-shadow duration-fast group-hover:ring-2">
							<AvatarImage src={session.characterAvatarUrl ?? undefined} alt="" />
							<AvatarFallback className="rounded-[var(--radius-lg)] bg-well">
								<IconMasksTheater size={20} stroke={1.75} />
							</AvatarFallback>
						</Avatar>
						<span className="line-clamp-2 w-full text-[0.6875rem] text-muted-foreground leading-tight">
							{session.characterTitle}
						</span>
					</Link>
				))}
			</div>
		</section>
	);
}
